import React from "react";

/**
 * Pure-SVG sparkline. Zero dependencies. Given a list of points, draws an
 * area-filled line chart that fits the parent's width, with an optional
 * highlighted peak dot and hover tooltips keyed by the x-axis label.
 *
 * Shape of each point: { date: string, total: number, count?: number }
 */
export default function Sparkline({
  data,
  height = 56,
  strokeColor = "#C1272D",
  fillColor = "rgba(193, 39, 45, 0.12)",
  showPeak = true,
  valuePrefix = "$",
}) {
  if (!data || data.length === 0) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-[11px] text-[#6B6B6B] italic"
      >
        No activity in window
      </div>
    );
  }

  const W = 300; // viewBox width — we let the SVG scale responsively
  const H = height;
  const PAD_X = 2;
  const PAD_Y = 4;

  const values = data.map((d) => Number(d.total) || 0);
  const max = Math.max(...values);
  const min = 0; // revenue can't go below zero; anchor baseline
  const span = max - min || 1;

  const stepX = (W - PAD_X * 2) / Math.max(1, data.length - 1);
  const y = (v) => H - PAD_Y - ((v - min) / span) * (H - PAD_Y * 2);
  const x = (i) => PAD_X + i * stepX;

  const line = data.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(2)} ${y(Number(d.total) || 0).toFixed(2)}`).join(" ");
  const area = `${line} L ${x(data.length - 1).toFixed(2)} ${H} L ${x(0).toFixed(2)} ${H} Z`;

  const peakIdx = values.indexOf(max);
  const hasAnyValue = max > 0;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full block"
        style={{ height }}
        role="img"
        aria-label="Revenue sparkline"
      >
        <path d={area} fill={fillColor} />
        <path d={line} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {/* Invisible hover points for tooltip */}
        {data.map((d, i) => (
          <g key={d.date}>
            <circle
              cx={x(i)}
              cy={y(Number(d.total) || 0)}
              r="6"
              fill="transparent"
            >
              <title>
                {formatDay(d.date)} — {valuePrefix}{(Number(d.total) || 0).toFixed(2)}
                {d.count ? ` (${d.count} tx)` : ""}
              </title>
            </circle>
          </g>
        ))}
        {showPeak && hasAnyValue && (
          <circle
            cx={x(peakIdx)}
            cy={y(values[peakIdx])}
            r="2.5"
            fill={strokeColor}
            stroke="white"
            strokeWidth="1"
          />
        )}
      </svg>
    </div>
  );
}

function formatDay(iso) {
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  } catch {
    return iso;
  }
}
